import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import api from "./api";
import { useAuth } from "./auth";

/**
 * Multi-tenant theming.
 *
 * Reads the tenant branding from `/api/tenants/me` and injects CSS variables
 * (--sw-primary, --sw-secondary, --sw-accent, --sw-surface, --sw-success,
 * --sw-warning, --sw-danger) at :root so every component automatically picks
 * up the tenant's palette. Also exposes the logo URLs + tagline via context.
 */

const TenantContext = createContext(null);

const DEFAULT_BRANDING = {
  primary: "#475569",
  secondary: "#94a3b8",
  accent: "#dac28a",
  surface: "#f6f6f6",
  text: "#323232",
  success: "#34c759",
  warning: "#f59e0b",
  danger: "#881a27",
  font_family: "Manrope",
  logo_url: "/sagewood-25y-light.png",
  logo_mark_url: "/sagewood-25y-mark.png",
};

const _loadedFonts = new Set();
function loadGoogleFont(family) {
  if (!family || _loadedFonts.has(family)) return;
  _loadedFonts.add(family);
  const href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, "+")}:wght@300;400;500;600;700&display=swap`;
  // Skip if already in DOM
  if (document.querySelector(`link[href*="${encodeURIComponent(family).replace(/%20/g, "+")}"]`)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
}


function applyBranding(branding) {
  const b = { ...DEFAULT_BRANDING, ...(branding || {}) };
  const root = document.documentElement;
  // Load the font dynamically from Google Fonts if not already on the page.
  if (b.font_family) loadGoogleFont(b.font_family);
  // CSS variables consumed across the app.
  root.style.setProperty("--tenant-primary", b.primary);
  root.style.setProperty("--tenant-secondary", b.secondary);
  root.style.setProperty("--tenant-accent", b.accent);
  root.style.setProperty("--tenant-surface", b.surface);
  root.style.setProperty("--tenant-text", b.text);
  root.style.setProperty("--tenant-success", b.success);
  root.style.setProperty("--tenant-warning", b.warning);
  root.style.setProperty("--tenant-danger", b.danger);
  // Map the long-standing Sagewood palette variables (used by the existing CSS)
  // onto the tenant palette so existing components inherit the new colors without
  // having to migrate hundreds of `var(--sw-…)` references in one pass.
  root.style.setProperty("--sw-dark-gold", b.primary);
  root.style.setProperty("--sw-light-gold", b.secondary);
  root.style.setProperty("--sw-lightest-gold", b.accent);
  root.style.setProperty("--sw-darkest-grey", b.text);
  root.style.setProperty("--sw-light-grey", b.surface);
  // Font family
  if (b.font_family) {
    root.style.setProperty("--tenant-font", `'${b.font_family}', system-ui, sans-serif`);
    document.body.style.fontFamily = `'${b.font_family}', system-ui, sans-serif`;
  }
  // Favicon (best-effort)
  if (b.logo_mark_url || b.logo_url) {
    const href = b.logo_mark_url || b.logo_url;
    const link = document.querySelector("link[rel='icon']");
    if (link) link.setAttribute("href", href);
  }
  // Page title
  if (b.tagline_for_title) document.title = b.tagline_for_title;
}

export function TenantProvider({ children }) {
  const { user } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      applyBranding(DEFAULT_BRANDING);
      setTenant(null);
      return;
    }
    setLoading(true);
    api.get("/tenants/me")
      .then((r) => {
        setTenant(r.data);
        const branding = r.data?.branding || DEFAULT_BRANDING;
        applyBranding({ ...branding, tagline_for_title: r.data?.tagline });
      })
      .catch(() => {
        applyBranding(DEFAULT_BRANDING);
      })
      .finally(() => setLoading(false));
  }, [user]);

  const value = useMemo(() => ({
    tenant,
    branding: tenant?.branding || DEFAULT_BRANDING,
    loading,
    isSuperAdmin: user?.role === "superadmin",
    refresh: async () => {
      const r = await api.get("/tenants/me");
      setTenant(r.data);
      applyBranding(r.data?.branding || DEFAULT_BRANDING);
    },
  }), [tenant, loading, user]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  // Return safe defaults when used outside the provider (e.g. landing/login). Avoids hooks order violations.
  if (!ctx) return { tenant: null, branding: DEFAULT_BRANDING, loading: false, isSuperAdmin: false, refresh: async () => {} };
  return ctx;
}
